// src/adminDashboard/shared/components/DataTable/DataTable.tsx
// Reusable data table component with advanced features

import {
    ChevronUp,
    ChevronDown,
    ChevronsUpDown,
    ChevronsLeft,
    ChevronsRight,
    ChevronLeft,
    ChevronRight,
    Download,
    Search,
    Filter,
    Grid,
    List,
    X,
    FileSpreadsheet,
    LucideIcon
} from 'lucide-react';
import React, { useState, useMemo } from 'react';

import { useIsMobile } from '../../hooks/useMediaQuery';
import EmptyState from '../EmptyState';

import BulkActionsToolbar, { BulkAction } from './BulkActionsToolbar';
import ColumnVisibilityMenu from './ColumnVisibilityMenu';
import MobileCard from './MobileCard';
import { exportToCSV, exportToExcel, ColumnDef } from './utils/exportHelpers';


export type { BulkAction } from './BulkActionsToolbar';
export type { ColumnDef } from './utils/exportHelpers';

type RowData = object;
type BivariantHandler<T extends (...args: never[]) => unknown> = {
    bivarianceHack(...args: Parameters<T>): ReturnType<T>;
}['bivarianceHack'];

const toRecord = (row: RowData): Record<string, unknown> => row as Record<string, unknown>;

interface PaginationConfig {
    page: number;
    perPage: number;
    total: number;
    onPageChange: (page: number) => void;
    onPerPageChange?: (perPage: number) => void;
}

interface DataTableProps<T extends RowData = RowData> {
    columns: ColumnDef[];
    data?: T[];
    isLoading?: boolean;
    emptyMessage?: string;
    emptyIcon?: LucideIcon;
    sortable?: boolean;
    pagination?: PaginationConfig | null;
    onRowClick?: BivariantHandler<(row: T) => void>;
    rowActions?: BivariantHandler<(row: T) => React.ReactNode>;
    selectedRows?: (string | number)[];
    onSelectRow?: (rowId: string | number) => void;
    onSelectAll?: (selected: boolean) => void;
    getId?: BivariantHandler<(row: T) => string | number>;
    exportable?: boolean;
    exportFilename?: string;
    bulkActions?: BulkAction[];
    hiddenColumns?: string[];
    onToggleColumn?: (key: string) => void;
    expandedRowId?: string | number | null;
    expandedRowContent?: BivariantHandler<(row: T) => React.ReactNode>;
    // Props from UserDashboard/index.jsx
    renderGridItem?: (item: T, rowActions?: (row: T) => React.ReactNode) => React.ReactNode;
    renderListItem?: (item: T, rowActions?: (row: T) => React.ReactNode) => React.ReactNode;
    defaultView?: 'list' | 'grid';
    searchPlaceholder?: string;
    searchKeys?: string[];
    filters?: { key: string; label: string; options: { value: string; label: string }[] }[];
    onExport?: (data: T[]) => void;
    className?: string;
    defaultPerPage?: number;
}

const DataTable = <T extends RowData = RowData,>({
    columns = [],
    data = [],
    isLoading = false,
    emptyMessage = 'No data found',
    emptyIcon,
    sortable = true,
    pagination = null,
    onRowClick,
    rowActions,
    selectedRows = [],
    onSelectRow,
    onSelectAll,
    getId = (row: T) => {
        const rowId = toRecord(row).id;
        return typeof rowId === 'string' || typeof rowId === 'number' ? rowId : '';
    },
    exportable = false,
    exportFilename = 'export',
    bulkActions = [],
    hiddenColumns = [],
    onToggleColumn,
    expandedRowId,
    expandedRowContent,
    renderGridItem,
    renderListItem,
    defaultView = 'list',
    searchPlaceholder = 'Search...',
    searchKeys = ['name', 'title'],
    filters: filterConfig = [],
    onExport,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    className = '',
    defaultPerPage = 10,
}: DataTableProps<T>) => {
    const [sortConfig, setSortConfig] = useState<{ key: string | null; direction: 'asc' | 'desc' }>({ key: null, direction: 'asc' });
    const [searchQuery, setSearchQuery] = useState('');
    const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
    const [viewMode, setViewMode] = useState<'list' | 'grid'>(defaultView);
    const [showFilters, setShowFilters] = useState(false);

    // Internal pagination state (only used if external pagination is not provided)
    const [internalPage, setInternalPage] = useState(1);
    const [internalPerPage, setInternalPerPage] = useState(defaultPerPage);
    const resolvePositiveInt = (value: unknown, fallback: number): number => {
        const numeric = Number(value);
        if (!Number.isFinite(numeric) || numeric <= 0) {
            return fallback;
        }
        return Math.floor(numeric);
    };

    const isMobile = useIsMobile();
    const getRowKey = (row: T, index: number): React.Key => {
        const rowId = toRecord(row).id;
        return typeof rowId === "string" || typeof rowId === "number" ? rowId : index;
    };

    // Filter visible columns
    const visibleColumns = useMemo(() => {
        return columns.filter(col => !hiddenColumns.includes(col.key));
    }, [columns, hiddenColumns]);

    // Handle sorting
    const handleSort = (key: string) => {
        if (!sortable) return;

        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    // Process data: search -> filter -> sort
    const processedData = useMemo(() => {
        let result = [...data];

        // 1. Internal Search
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            result = result.filter(item =>
                searchKeys.some(key => {
                    const value = key.split('.').reduce<unknown>(
                        (obj, k) =>
                            obj && typeof obj === 'object'
                                ? (obj as Record<string, unknown>)[k]
                                : undefined,
                        item
                    );
                    return value?.toString().toLowerCase().includes(query);
                })
            );
        }

        // 2. Internal Filters
        Object.entries(activeFilters).forEach(([key, value]) => {
            if (value && value !== 'all') {
                result = result.filter(item => {
                    const itemValue = key.split('.').reduce<unknown>(
                        (obj, k) =>
                            obj && typeof obj === 'object'
                                ? (obj as Record<string, unknown>)[k]
                                : undefined,
                        item
                    );
                    return itemValue?.toString().toLowerCase() === value.toLowerCase();
                });
            }
        });

        // 3. Internal Sort
        const sortKey = sortConfig.key;
        if (sortKey) {
            result.sort((a, b) => {
                const column = columns.find((col) => col.key === sortKey);
                const aValue = column?.getValue ? column.getValue(a) : toRecord(a)[sortKey];
                const bValue = column?.getValue ? column.getValue(b) : toRecord(b)[sortKey];

                if (aValue === bValue) return 0;
                if (aValue === null || aValue === undefined) return 1;
                if (bValue === null || bValue === undefined) return -1;

                const comparison = aValue < bValue ? -1 : 1;
                return sortConfig.direction === 'asc' ? comparison : -comparison;
            });
        }

        return result;
    }, [data, searchQuery, searchKeys, activeFilters, sortConfig, columns]);

    // Internal vs External Pagination
    const fallbackPerPage = resolvePositiveInt(defaultPerPage, 10);
    const effectiveTotal = pagination
        ? Math.max(0, Number(pagination.total) || 0)
        : processedData.length;
    const effectivePerPage = pagination
        ? resolvePositiveInt(pagination.perPage, fallbackPerPage)
        : resolvePositiveInt(internalPerPage, fallbackPerPage);
    const totalPages = Math.max(1, Math.ceil(effectiveTotal / effectivePerPage));
    const effectivePage = pagination
        ? Math.min(Math.max(resolvePositiveInt(pagination.page, 1), 1), totalPages)
        : Math.min(Math.max(resolvePositiveInt(internalPage, 1), 1), totalPages);
    const startItem = effectiveTotal === 0 ? 0 : (effectivePage - 1) * effectivePerPage + 1;
    const endItem = effectiveTotal === 0 ? 0 : Math.min(effectivePage * effectivePerPage, effectiveTotal);

    const paginatedData = useMemo(() => {
        if (pagination) return processedData; // If external pagination is used, assume processedData is already paginated or handled externally

        // Internal pagination
        const start = (effectivePage - 1) * effectivePerPage;
        return processedData.slice(start, start + effectivePerPage);
    }, [processedData, pagination, effectivePage, effectivePerPage]);

    // Export handlers
    const handleExportCSV = () => {
        if (onExport) {
            onExport(processedData);
            return;
        }
        exportToCSV(processedData, visibleColumns, exportFilename);
    };

    const handleExportExcel = () => {
        exportToExcel(processedData, visibleColumns, exportFilename);
    };

    // Render sort icon
    const renderSortIcon = (key: string) => {
        if (sortConfig.key !== key) {
            return <ChevronsUpDown size={14} className="text-slate-400" />;
        }
        return sortConfig.direction === 'asc' ? (
            <ChevronUp size={14} className="text-primary" />
        ) : (
            <ChevronDown size={14} className="text-primary" />
        );
    };

    // Loading skeleton
    if (isLoading) {
        return (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200">
                        <thead className="bg-slate-50">
                            <tr>
                                {visibleColumns.map((col) => (
                                    <th
                                        key={col.key}
                                        className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider"
                                    >
                                        <div className="h-4 w-20 bg-slate-200 rounded animate-pulse" />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200">
                            {new Array(5).fill(null).map((_, i) => (
                                <tr key={`skeleton-${i}`}>
                                    {visibleColumns.map((col) => (
                                        <td key={`skeleton-col-${col.key}`} className="px-4 py-3.5">
                                            <div className="h-4 bg-slate-100 rounded animate-pulse" />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    // Empty state
    if (data.length === 0) {
        return (
            <div className="bg-white rounded-xl border border-slate-200 p-8">
                <EmptyState message={emptyMessage} icon={emptyIcon} />
            </div>
        );
    }

    // remove duplicate totalPages
    const hasSelection = selectedRows.length > 0;

    return (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {/* Bulk Actions Toolbar */}
            {hasSelection && bulkActions.length > 0 && (
                <BulkActionsToolbar
                    selectedCount={selectedRows.length}
                    onClearSelection={() => onSelectAll?.(false)}
                    actions={bulkActions.map(action => ({
                        ...action,
                        onClick: () => action.onClick(selectedRows)
                    }))}
                />
            )}

            {/* Search and Filters Toolbar */}
            <div className="px-4 py-3 border-b border-slate-100 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 bg-white">
                {/* Search */}
                <div className="relative flex-1">
                    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={searchPlaceholder}
                        className="w-full pl-10 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-slate-50"
                    />
                    {searchQuery && (
                        <button
                            onClick={() => setSearchQuery('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                            <X size={16} />
                        </button>
                    )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                    {/* Filters Toggle */}
                    {filterConfig.length > 0 && (
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${showFilters || Object.values(activeFilters).some(v => v && v !== 'all')
                                ? 'border-primary bg-primary/5 text-primary'
                                : 'border-slate-200 text-slate-600 hover:border-slate-300'
                                }`}
                        >
                            <Filter size={16} />
                            <span className="hidden sm:inline">Filters</span>
                        </button>
                    )}

                    {/* View Toggle */}
                    {renderGridItem && renderListItem && (
                        <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden">
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-2 ${viewMode === 'list' ? 'bg-slate-100 text-primary' : 'text-slate-400 hover:text-slate-600'}`}
                            >
                                <List size={18} />
                            </button>
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`p-2 ${viewMode === 'grid' ? 'bg-slate-100 text-primary' : 'text-slate-400 hover:text-slate-600'}`}
                            >
                                <Grid size={18} />
                            </button>
                        </div>
                    )}

                    {onToggleColumn && (
                        <ColumnVisibilityMenu
                            columns={columns}
                            hiddenColumns={hiddenColumns}
                            onToggle={onToggleColumn}
                        />
                    )}

                    {exportable && (
                        <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden">
                            <button
                                onClick={handleExportCSV}
                                className="p-2 text-slate-600 hover:bg-slate-50 border-r border-slate-200"
                                title="Export to CSV"
                            >
                                <Download size={16} />
                            </button>
                            <button
                                onClick={handleExportExcel}
                                className="p-2 text-slate-600 hover:bg-slate-50"
                                title="Export to Excel"
                            >
                                <FileSpreadsheet size={16} />
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Filter Panel */}
            {showFilters && filterConfig.length > 0 && (
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex flex-wrap items-center gap-3">
                    {filterConfig.map((filter) => (
                        <div key={filter.key} className="flex items-center gap-2">
                            <label htmlFor={`filter-${filter.key}`} className="text-sm text-slate-600 font-medium">{filter.label}:</label>
                            <select
                                id={`filter-${filter.key}`}
                                value={activeFilters[filter.key] || 'all'}
                                onChange={(e) => setActiveFilters(prev => ({ ...prev, [filter.key]: e.target.value }))}
                                className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/20"
                            >
                                <option value="all">All</option>
                                {filter.options.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                    ))}
                    {Object.values(activeFilters).some(v => v && v !== 'all') && (
                        <button
                            onClick={() => setActiveFilters({})}
                            className="text-sm text-red-600 hover:text-red-700 font-medium"
                        >
                            Clear All
                        </button>
                    )}
                </div>
            )}

            {/* Content View */}
            {viewMode === 'grid' && renderGridItem ? (
                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {paginatedData.map((item: T, index: number) => (
                        <div key={getRowKey(item, index)}>
                            {renderGridItem(item, rowActions)}
                        </div>
                    ))}
                </div>
            ) : viewMode === 'list' && renderListItem ? (
                <div className="p-4 space-y-3">
                    {paginatedData.map((item: T, index: number) => (
                        <div key={getRowKey(item, index)}>
                            {renderListItem(item, rowActions)}
                        </div>
                    ))}
                </div>
            ) : isMobile ? (
                <div className="p-4 space-y-3">
                    {paginatedData.map((row: T, rowIndex: number) => {
                        const rowId = getId(row);
                        const isSelected = selectedRows.includes(rowId);

                        return (
                            <MobileCard
                                key={rowId ?? rowIndex}
                                row={row}
                                columns={visibleColumns}
                                {...(onRowClick ? { onRowClick } : {})}
                                {...(rowActions ? { rowActions } : {})}
                                isSelected={isSelected}
                                {...(onSelectRow ? { onSelectRow } : {})}
                                getId={getId}
                            />
                        );
                    })}
                </div>
            ) : (
                /* Desktop View - Table Layout */
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200">
                        <thead className="bg-slate-50">
                            <tr>
                                {/* Selection checkbox */}
                                {onSelectAll && (
                                    <th className="px-4 py-3 w-10">
                                        <input
                                            type="checkbox"
                                            checked={selectedRows.length === data.length && data.length > 0}
                                            onChange={(e) => onSelectAll(e.target.checked)}
                                            className="rounded border-slate-300 text-primary focus:ring-primary"
                                        />
                                    </th>
                                )}

                                {visibleColumns.map((col) => (
                                    <th
                                        key={col.key}
                                        className={`
                    px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider
                    ${col.sortable !== false && sortable ? 'cursor-pointer hover:bg-slate-100' : ''}
                    ${col.align === 'right' ? 'text-right' : ''}
                    ${col.align === 'center' ? 'text-center' : ''}
                  `}
                                        onClick={() => col.sortable !== false && handleSort(col.key)}
                                        style={{ width: col.width }}
                                    >
                                        <div className={`flex items-center gap-1.5 ${col.align === 'right' ? 'justify-end' : ''}`}>
                                            {col.header}
                                            {col.sortable !== false && sortable && renderSortIcon(col.key)}
                                        </div>
                                    </th>
                                ))}

                                {/* Actions column */}
                                {rowActions && (
                                    <th className="px-4 py-3 w-16 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                )}
                            </tr>
                        </thead>

                        <tbody className="divide-y divide-slate-200 bg-white">
                            {paginatedData.map((row: T, rowIndex: number) => {
                                const rowId = getId(row);
                                const isSelected = selectedRows.includes(rowId);

                                return (
                                    <React.Fragment key={rowId || rowIndex}>
                                        <tr
                                            className={`
                    ${onRowClick ? 'cursor-pointer hover:bg-slate-50' : ''}
                    ${isSelected ? 'bg-primary/5' : ''}
                    transition-colors
                  `}
                                            onClick={() => onRowClick?.(row)}
                                        >
                                            {/* Selection checkbox */}
                                            {onSelectRow && (
                                                <td className="px-4 py-3.5" onClick={(e) => e.stopPropagation()}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => onSelectRow(rowId)}
                                                        className="rounded border-slate-300 text-primary focus:ring-primary"
                                                    />
                                                </td>
                                            )}

                                            {visibleColumns.map((col) => (
                                                <td
                                                    key={col.key}
                                                    className={`
                        px-4 py-3.5 text-sm text-slate-700
                        ${col.align === 'right' ? 'text-right' : ''}
                        ${col.align === 'center' ? 'text-center' : ''}
                      `}
                                                >
                                                    {col.render ? col.render(row, rowIndex) : (
                                                        (() => {
                                                            const rawValue = toRecord(row)[col.key] ?? '';
                                                            return (rawValue !== null && typeof rawValue === 'object')
                                                                ? JSON.stringify(rawValue)
                                                                : String(rawValue);
                                                        })()
                                                    )}
                                                </td>
                                            ))}

                                            {/* Row actions */}
                                            {rowActions && (
                                                <td className="px-4 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                                                    {rowActions(row)}
                                                </td>
                                            )}
                                        </tr>

                                        {/* Expanded Content */}
                                        {
                                            expandedRowId === rowId && expandedRowContent && (
                                                <tr key={`${rowId}-expanded`} className="bg-slate-50/50">
                                                    <td colSpan={visibleColumns.length + (onSelectRow ? 1 : 0) + (rowActions ? 1 : 0)} className="px-4 py-3 border-t border-slate-100">
                                                        {expandedRowContent(row)}
                                                    </td>
                                                </tr>
                                            )
                                        }
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )
            }

            {/* Pagination */}
            {(pagination || totalPages > 1) && (
                <div className="flex flex-col sm:flex-row gap-4 items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
                    <div className="flex items-center gap-4">
                        <div className="text-sm text-slate-500 text-center sm:text-left">
                            Showing{' '}
                            <span className="font-medium">
                                {startItem}
                            </span>{' '}
                            to{' '}
                            <span className="font-medium">
                                {endItem}
                            </span>{' '}
                            of <span className="font-medium">{effectiveTotal}</span> results
                        </div>

                        {/* Per-page selector */}
                        {(pagination?.onPerPageChange || !pagination) && (
                            <div className="flex items-center gap-2">
                                <label className="text-sm text-slate-500">Per page:</label>
                                <select
                                    value={effectivePerPage}
                                    onChange={(e) => {
                                        const val = Number(e.target.value);
                                        if (pagination?.onPerPageChange) {
                                            pagination.onPerPageChange(val);
                                        } else {
                                            setInternalPerPage(val);
                                            setInternalPage(1);
                                        }
                                    }}
                                    className="px-2 py-1 border border-slate-200 rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/20"
                                >
                                    <option value="10">10</option>
                                    <option value="20">20</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => {
                                if (pagination) {
                                    pagination.onPageChange(1);
                                } else {
                                    setInternalPage(1);
                                }
                            }}
                            disabled={effectivePage <= 1}
                            className="p-2 rounded-lg border border-slate-200 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                            title="First page"
                        >
                            <ChevronsLeft size={16} />
                        </button>
                        <button
                            onClick={() => {
                                if (pagination) {
                                    pagination.onPageChange(Math.max(1, effectivePage - 1));
                                } else {
                                    setInternalPage(p => Math.max(1, p - 1));
                                }
                            }}
                            disabled={effectivePage <= 1}
                            className="p-2 rounded-lg border border-slate-200 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Previous page"
                        >
                            <ChevronLeft size={16} />
                        </button>

                        <span className="text-sm text-slate-600">
                            Page {effectivePage} of {totalPages}
                        </span>

                        <button
                            onClick={() => {
                                if (pagination) {
                                    pagination.onPageChange(Math.min(totalPages, effectivePage + 1));
                                } else {
                                    setInternalPage(p => Math.min(totalPages, p + 1));
                                }
                            }}
                            disabled={effectivePage >= totalPages}
                            className="p-2 rounded-lg border border-slate-200 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Next page"
                        >
                            <ChevronRight size={16} />
                        </button>
                        <button
                            onClick={() => {
                                if (pagination) {
                                    pagination.onPageChange(totalPages);
                                } else {
                                    setInternalPage(totalPages);
                                }
                            }}
                            disabled={effectivePage >= totalPages}
                            className="p-2 rounded-lg border border-slate-200 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Last page"
                        >
                            <ChevronsRight size={16} />
                        </button>
                    </div>
                </div>
            )}
        </div >
    );
};

export default DataTable;
